/**
 * Streams one Cinch AI turn from the mcp-proxy (CINCHAI-108).
 *
 * The proxy speaks OpenAI-style SSE (`data: {chat.completion.chunk}` lines, then `data: [DONE]`).
 * The native app also receives `{"object": "cinch.event", "type": ...}` frames on the same stream
 * (thread, saved, navigate, ...), which Open WebUI never gets. Auth is the app's own token
 * (`Authorization: Token`), verified by the proxy against the engine (CINCHAI-106).
 *
 * fetch + ReadableStream rather than EventSource: EventSource can't POST or set headers.
 *
 * Native turns run as server-side jobs (CINCHAI-119): the first frame is a `turn` event with the
 * job id. A reload can reattach with `followCinchAiTurn` (the frames from the start, then live),
 * and Stop is `stopCinchAiTurn`; closing the stream alone no longer stops the turn.
 */

// Known gap: where the proxy runs outside local dev is not decided (discovery §14 risk 1).
// CINCH_AI_URL is a build-time global (webpack DefinePlugin); local dev uses the proxy on :8765.
export const cinchAiUrl = () => typeof CINCH_AI_URL !== 'undefined' && CINCH_AI_URL || 'http://localhost:8765';
export class CinchAiError extends Error {
  constructor(message, {
    code,
    status,
    details
  } = {}) {
    super(message);
    this.code = code;
    this.status = status;
    // e.g. a usage limit's `window` and `resets_when` (CINCHAI-121)
    this.details = details || {};
  }
}

/**
 * @param {object} args
 * @param {string} args.token the user's app token
 * @param {object} args.body chat-completions body: messages, thread_id, cinch_company_id, page_context, allowed_pages
 * @param {AbortSignal} args.signal Stop
 * @param {(text: string) => void} args.onText each text delta
 * @param {(event: object) => void} args.onEvent each cinch.event frame
 * @returns {Promise<{usage?: object}>}
 */
export async function streamCinchAiTurn({
  token,
  body,
  signal,
  onText,
  onEvent
}) {
  const response = await fetch(`${cinchAiUrl()}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`
    },
    body: JSON.stringify({
      model: 'cinch-ai',
      stream: true,
      ...body
    }),
    signal
  });
  return readTurnStream(response, {
    onText,
    onEvent
  });
}

/** Reattach to a running turn: every frame from `after` (0 = the whole turn so far), then live. */
export async function followCinchAiTurn({
  token,
  turnId,
  after = 0,
  signal,
  onText,
  onEvent
}) {
  const response = await fetch(`${cinchAiUrl()}/v1/turns/${turnId}/events?after=${after}`, {
    headers: {
      Authorization: `Token ${token}`
    },
    signal
  });
  return readTurnStream(response, {
    onText,
    onEvent
  });
}

/** The thread's still-running turn (`{turn_id, frames}`), or null. */
export async function getOpenCinchAiTurn({
  token,
  threadId
}) {
  const response = await fetch(`${cinchAiUrl()}/v1/threads/${threadId}/open-turn`, {
    headers: {
      Authorization: `Token ${token}`
    }
  });
  return response.ok ? response.json() : null;
}

/** Stop a turn on the server; what was said is saved with "_Stopped._". */
export async function stopCinchAiTurn({
  token,
  turnId
}) {
  await fetch(`${cinchAiUrl()}/v1/turns/${turnId}/stop`, {
    method: 'POST',
    headers: {
      Authorization: `Token ${token}`
    }
  });
}
async function readTurnStream(response, {
  onText,
  onEvent
}) {
  if (!response.ok) {
    let error = {};
    try {
      error = (await response.json())?.error || {};
    } catch (e) {
      // Not JSON (a gateway error page); fall through to the generic message.
    }
    throw new CinchAiError(error.message || `Cinch AI is unavailable (${response.status}).`, {
      code: error.code,
      status: response.status,
      details: error
    });
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let usage;
  const handleFrame = frame => {
    const data = frame.split('\n').filter(line => line.startsWith('data: ')).map(line => line.slice(6)).join('\n');
    if (!data || data === '[DONE]') return;
    let parsed;
    try {
      parsed = JSON.parse(data);
    } catch (e) {
      return;
    }
    if (parsed.object === 'cinch.event') {
      onEvent?.(parsed);
      return;
    }
    const text = parsed.choices?.[0]?.delta?.content;
    if (text) onText?.(text);
    if (parsed.usage) usage = parsed.usage;
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const {
      value,
      done
    } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, {
      stream: true
    });
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      handleFrame(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) handleFrame(buffer);
  return {
    usage
  };
}

/**
 * Download a file the agent built (CINCHAI-125): a fresh signed link for this user, fetched with
 * the token (the proxy checks both), saved from a blob so the token never sits in a URL.
 */
export async function downloadCinchAiFile({
  token,
  fileId,
  name
}) {
  const auth = {
    Authorization: `Token ${token}`
  };
  const base = `${cinchAiUrl()}/files/${encodeURIComponent(fileId)}/${encodeURIComponent(name)}`;
  const linkResponse = await fetch(`${base}/link`, {
    method: 'POST',
    headers: auth
  });
  if (!linkResponse.ok) throw new Error(linkResponse.status === 404 ? 'This file is no longer available.' : 'Download failed.');
  const {
    url
  } = await linkResponse.json();
  const fileResponse = await fetch(`${cinchAiUrl()}${url}`, {
    headers: auth
  });
  if (!fileResponse.ok) throw new Error(fileResponse.status === 410 ? 'The download link expired. Try again.' : 'Download failed.');
  const blob = await fileResponse.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = name;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}